import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { User } from '../types';
import { calculateAge, convertToDisplayFormat, convertToIsoFormat, validateDateFormat } from '../utils/ageCalculator';
import { getAvatarById } from '../utils/avatarUtils';
import AvatarSelector from './AvatarSelector';

const ProfileEdit: React.FC = () => {
  const { currentUser, logout, refreshUserData: refreshGlobalUserData } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [refreshedUser, setRefreshedUser] = useState<User | null>(currentUser);
  
  const [formData, setFormData] = useState({
    name: refreshedUser?.name || '',
    nickname: refreshedUser?.nickname || '',
    instrument: refreshedUser?.instrument || '',
    birthdate: refreshedUser?.birthdate || '',
    practiceSessionDuration: refreshedUser?.practiceSessionDuration || 30,
    practiceSessionsPerWeek: refreshedUser?.practiceSessionsPerWeek || 5,
  });
  
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(refreshedUser?.avatarId || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [goalChangesRequested, setGoalChangesRequested] = useState(false);

  // Initialize form data when user data is loaded
  useEffect(() => {
    if (refreshedUser) {
      setFormData({
        name: refreshedUser.name || '',
        nickname: refreshedUser.nickname || '',
        instrument: refreshedUser.instrument || '',
        birthdate: refreshedUser.birthdate || '',
        practiceSessionDuration: refreshedUser.practiceSessionDuration || 30,
        practiceSessionsPerWeek: refreshedUser.practiceSessionsPerWeek || 5,
      });
      setSelectedAvatarId(refreshedUser?.avatarId || null);
    }
  }, [refreshedUser]);

  // Load user data on component mount
  useEffect(() => {
    refreshUserData();
  }, []);

  // Refresh user data from Firestore
  const refreshUserData = async () => {
    if (!currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const updatedUser = {
          uid: currentUser.uid,
          ...userData
        } as User;
        setRefreshedUser(updatedUser);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'birthdate' && value) {
      // Convert DD-MM-YYYY to YYYY-MM-DD for storage
      const isoFormatDate = validateDateFormat(value) ? convertToIsoFormat(value) : value;
      setFormData(prev => ({
        ...prev,
        [name]: isoFormatDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: (name === 'practiceSessionDuration' || name === 'practiceSessionsPerWeek') 
          ? parseInt(value) || 0 
          : value
      }));
    }
  };


  const checkNicknameUniqueness = async (nickname: string, currentUserId: string): Promise<boolean> => {
    if (!currentUserId) return false;
    try {
      const nicknameQuery = query(
        collection(db, 'users'),
        where('nickname', '==', nickname.trim())
      );
      const querySnapshot = await getDocs(nicknameQuery);
      
      // Check if any user other than the current user has this nickname
      const conflictingUsers = querySnapshot.docs.filter(doc => doc.id !== currentUserId);
      return conflictingUsers.length === 0; // Returns true if nickname is unique
    } catch (error) {
      console.error('Error checking nickname uniqueness:', error);
      return false; // If there's an error, assume nickname is not unique to be safe
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
      setError('No user logged in');
      return;
    }

    // Check nickname uniqueness (only if nickname has changed)
    if (formData.nickname !== refreshedUser?.nickname) {
      const isNicknameUnique = await checkNicknameUniqueness(formData.nickname, currentUser.uid);
      if (!isNicknameUnique) {
        setError('This nickname is already taken. Please choose a different one.');
        return;
      }
    }

    // Validate birthdate for students
    if (currentUser?.role === 'student' && formData.birthdate && !validateDateFormat(convertToDisplayFormat(formData.birthdate))) {
      setError('Please enter a valid birthdate in DD-MM-YYYY format');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

          // Updating profile for user

      // Handle avatar selection (no file upload needed)
      const avatarId = selectedAvatarId;

      // Update user profile in Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Check if goal values have changed for students
      const goalsChanged = currentUser?.role === 'student' && (
        formData.practiceSessionDuration !== (refreshedUser?.practiceSessionDuration || 30) ||
        formData.practiceSessionsPerWeek !== (refreshedUser?.practiceSessionsPerWeek || 5)
      );

      // Check if student has linked teachers
      const hasLinkedTeachers = currentUser?.role === 'student' && 
        refreshedUser?.linkedTeachers && 
        refreshedUser.linkedTeachers.length > 0;

      if (goalsChanged && currentUser?.role === 'student' && hasLinkedTeachers) {
        // For students WITH linked teachers, request goal changes instead of direct update
        await requestGoalChanges();
        
        // Update other fields normally
        const updateData: any = {
          name: formData.name,
          nickname: formData.nickname,
          instrument: formData.instrument,
          birthdate: formData.birthdate
        };
        
        if (avatarId !== null && avatarId !== undefined) {
          updateData.avatarId = avatarId;
        }
        
        await updateDoc(userRef, updateData);
      } else {
        // For teachers, students WITHOUT linked teachers, or when goals haven't changed, update normally
        const updateData: any = {
          ...formData
        };
        
        if (avatarId !== null && avatarId !== undefined) {
          updateData.avatarId = avatarId;
        }
        
        await updateDoc(userRef, updateData);
      }

      // Update local state immediately to show changes
      const updatedUser = {
        ...refreshedUser,
        ...formData,
        avatarId: avatarId
      } as User;
      setRefreshedUser(updatedUser);

      // Set appropriate success message based on whether approval was needed
      if (goalsChanged && currentUser?.role === 'student' && hasLinkedTeachers) {
        setSuccess('Profile updated successfully! Goal change requests have been sent to your teacher for approval.');
      } else if (goalsChanged && currentUser?.role === 'student' && !hasLinkedTeachers) {
        setSuccess('Profile updated successfully! Your practice goals have been updated immediately.');
      } else {
        setSuccess('Profile updated successfully!');
      }
      
      // Refresh global user data to update all components
      await refreshGlobalUserData();
      
      // Also refresh local data after a short delay to ensure consistency
      setTimeout(async () => {
        await refreshUserData();
      }, 1000);
      
      // Redirect after a longer delay
      setTimeout(() => {
        if (currentUser?.role === 'teacher') {
          navigate('/teacher/dashboard');
        } else {
          navigate('/profile');
        }
      }, 2000);

    } catch (err: any) {
      console.error('Error updating profile:', err);
      console.error('Error details:', {
        code: err.code,
        message: err.message,
        stack: err.stack
      });
      
      let errorMessage = 'Failed to update profile. Please try again.';
      
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your account status.';
      } else if (err.code === 'unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };


  // Handle goal change requests for students
  const requestGoalChanges = async () => {
    if (!currentUser || currentUser.role !== 'student') return;

    try {
      const goalChangeRequest = {
        practiceSessionDuration: formData.practiceSessionDuration,
        practiceSessionsPerWeek: formData.practiceSessionsPerWeek,
        requestedAt: new Date(),
        status: 'pending' as const
      };

      await updateDoc(doc(db, 'users', currentUser.uid), {
        pendingGoalChanges: goalChangeRequest
      });

      setGoalChangesRequested(true);
      setSuccess('Goal change request sent to your teacher for approval.');
      
      // Refresh user data to show updated status
      await refreshUserData();
    } catch (error) {
      console.error('Error requesting goal changes:', error);
      setError('Failed to send goal change request. Please try again.');
    }
  };

  if (!refreshedUser) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <nav className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-2xl mx-auto px-4 sm:px-0">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="text-3xl mr-3">✏️</div>
                <h1 className="text-xl font-bold text-white">
                  Edit Profile
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(refreshedUser.role === 'teacher' ? '/teacher/dashboard' : '/profile')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Back
              </button>
              <button
                onClick={handleLogout}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Edit Your Profile
            </h2>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
                <p className="text-green-600 text-sm">{success}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Avatar Section */}
              <div className="flex items-center space-x-6">
                <div className="flex-shrink-0">
                  {selectedAvatarId ? (
                    <div 
                      className="h-20 w-20 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-4xl"
                      style={{ 
                        backgroundColor: getAvatarById(selectedAvatarId)?.color || '#8B4513'
                      }}
                    >
                      {getAvatarById(selectedAvatarId)?.emoji || '🎹'}
                    </div>
                  ) : (
                    <div className="h-20 w-20 rounded-full bg-gray-300 flex items-center justify-center">
                      <svg className="h-8 w-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile Avatar
                  </label>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowAvatarSelector(true)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      {selectedAvatarId ? 'Change Avatar' : 'Select Avatar'}
                    </button>
                    {selectedAvatarId && (
                      <button
                        type="button"
                        onClick={() => setSelectedAvatarId(null)}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose from our safe, pre-approved avatar collection. No file uploads required.
                  </p>
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="nickname" className="block text-sm font-medium text-gray-700">
                    Nickname *
                  </label>
                  <input
                    type="text"
                    name="nickname"
                    id="nickname"
                    required
                    value={formData.nickname}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="instrument" className="block text-sm font-medium text-gray-700">
                    Instrument *
                  </label>
                  <select
                    name="instrument"
                    id="instrument"
                    required
                    value={formData.instrument}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Select an instrument</option>
                    <option value="Bassoon">Bassoon</option>
                    <option value="Cello">Cello</option>
                    <option value="Clarinet">Clarinet</option>
                    <option value="Double Bass">Double Bass</option>
                    <option value="Euphonium">Euphonium</option>
                    <option value="Flute">Flute</option>
                    <option value="Guitar">Guitar</option>
                    <option value="Horn">Horn</option>
                    <option value="Oboe">Oboe</option>
                    <option value="Percussion">Percussion</option>
                    <option value="Piano">Piano</option>
                    <option value="Saxophone">Saxophone</option>
                    <option value="Trombone">Trombone</option>
                    <option value="Trumpet">Trumpet</option>
                    <option value="Tuba">Tuba</option>
                    <option value="Viola">Viola</option>
                    <option value="Violin">Violin</option>
                    <option value="Voice">Voice</option>
                  </select>
                </div>

                {/* Birth Date - Only for students */}
                {currentUser?.role === 'student' && (
                  <div>
                    <label htmlFor="birthdate" className="block text-sm font-medium text-gray-700">
                      Birth Date (DD-MM-YYYY) *
                    </label>
                    <input
                      type="text"
                      name="birthdate"
                      id="birthdate"
                      required
                      placeholder="15-03-2010"
                      value={formData.birthdate ? convertToDisplayFormat(formData.birthdate) : ''}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    {formData.birthdate && validateDateFormat(convertToDisplayFormat(formData.birthdate)) && (
                      <p className="mt-1 text-sm text-gray-500">
                        Age: {calculateAge(formData.birthdate)} years old
                      </p>
                    )}
                    {formData.birthdate && !validateDateFormat(convertToDisplayFormat(formData.birthdate)) && (
                      <p className="mt-1 text-sm text-red-500">
                        Please enter date in DD-MM-YYYY format
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Practice Preferences */}
              <div className="bg-blue-50 px-4 py-5 rounded-md">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Practice Preferences</h3>
                
                {/* Goal Approval Notice for Students */}
                {currentUser?.role === 'student' && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          {refreshedUser?.linkedTeachers && refreshedUser.linkedTeachers.length > 0 ? (
                            <>
                              <strong>Note:</strong> Changes to your practice goals will be sent to your teacher for approval. Your current goals will remain active until approved.
                            </>
                          ) : (
                            <>
                              <strong>Note:</strong> You can freely edit your practice goals since you don't have a linked teacher. Changes will be applied immediately.
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="practiceSessionDuration" className="block text-sm font-medium text-gray-700">
                      Daily Practice Goal (minutes)
                    </label>
                    <select
                      name="practiceSessionDuration"
                      id="practiceSessionDuration"
                      value={formData.practiceSessionDuration.toString()}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="1">1 minute (Testing)</option>
                      <option value="15">15 minutes</option>
                      <option value="20">20 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">60 minutes</option>
                      <option value="90">90 minutes</option>
                      <option value="120">120 minutes</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="practiceSessionsPerWeek" className="block text-sm font-medium text-gray-700">
                      Practice Frequency (times per week)
                    </label>
                    <select
                      name="practiceSessionsPerWeek"
                      id="practiceSessionsPerWeek"
                      value={formData.practiceSessionsPerWeek}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value={3}>3 times per week</option>
                      <option value={4}>4 times per week</option>
                      <option value={5}>5 times per week</option>
                      <option value={6}>6 times per week</option>
                      <option value={7}>Every day</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Week Start Day
                    </label>
                    <div className="mt-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-600">
                      {refreshedUser?.weekStartDay || 'Sunday'} (Fixed for leaderboard consistency)
                    </div>
                  </div>
                </div>
                
                {/* Goal Change Status for Students */}
                {currentUser?.role === 'student' && refreshedUser?.pendingGoalChanges && (
                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        refreshedUser.pendingGoalChanges.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800'
                          : refreshedUser.pendingGoalChanges.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {refreshedUser.pendingGoalChanges.status === 'pending' && '⏳ Pending Approval'}
                        {refreshedUser.pendingGoalChanges.status === 'approved' && '✅ Approved'}
                        {refreshedUser.pendingGoalChanges.status === 'rejected' && '❌ Rejected'}
                      </span>
                      <span className="text-xs text-gray-500">
                        Requested: {refreshedUser.pendingGoalChanges.requestedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                      </span>
                    </div>
                    {refreshedUser.pendingGoalChanges.reviewNotes && (
                      <p className="mt-2 text-xs text-gray-600">
                        <strong>Teacher Note:</strong> {refreshedUser.pendingGoalChanges.reviewNotes}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Role Display */}
              <div className="bg-gray-50 px-4 py-3 rounded-md">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      Account Type: <span className="capitalize">{refreshedUser.role}</span>
                    </p>
                    <p className="text-sm text-gray-500">
                      This cannot be changed after registration
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate(refreshedUser.role === 'teacher' ? '/teacher/dashboard' : '/profile')}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Avatar Selector Modal */}
      {showAvatarSelector && (
        <AvatarSelector
          currentUser={currentUser}
          onAvatarSelect={(avatarId) => {
            setSelectedAvatarId(avatarId);
            setShowAvatarSelector(false);
          }}
          onClose={() => setShowAvatarSelector(false)}
        />
      )}
    </div>
  );
};

export default ProfileEdit;
